import { useState } from "react";

// Third Party
import { createFileRoute } from "@tanstack/react-router";
import { Formik, Field } from "formik";
import { useQueryClient, useMutation, useQueries } from "@tanstack/react-query";

import { useAccessPermission } from "@/helpers/acessPermission";

// UI// helpers

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import AlertModal from "@/components/AlertModal";
import TableSkeleton from "@/components/skeleton/TableSkeleton";

// Icons
import { PlusCircle, Pen } from "lucide-react";

// Services and Types
import {
  getEmployee,
  createEmployee,
  updateEmployee,
  updateEmployeeStatus,
} from "@/services/soe";
import type { EmployeeList, MutateEmployee } from "@/types/soe";

// Type for employee data where password is optional (for updates)
type MutateEmployeeOptionalPassword = Omit<MutateEmployee, "password"> & {
  password?: string;
};
import { getRoles, getStatus } from "@/services/master";
import type { Roles } from "@/types/master";
import formatDate from "@/utils/formatDate";
import DatePicker from "@/components/ui/DatePicker";
import { clearLocalStorage } from "@/helpers/accessLocalStorage";

export const Route = createFileRoute(
  "/_layout-private/account-management/soe/_information/account/"
)({
  component: RouteComponent,
});
function RouteComponent() {
  const companyId = localStorage.getItem("companyId");
  const id = companyId?.replace(/^"(.+)"$/, "$1") || "";

  // const { id } = Route.useParams();
  const queryClient = useQueryClient();

  const [openModal, setOpenModal] = useState(false);
  const { toast } = useToast();

  const [selectedData, setSelectedData] = useState({} as EmployeeList);
  const [statusId, setStatusId] = useState("");

  const { company_management } = useAccessPermission();

  // Query

  const [
    { data: employeesRes, isLoading: employeeLoading, error: employeeError },
    { data: rolesRes, isLoading: roleLoading, error: rolesError },
    { data: statusRes, isLoading: statusLoading, error: statusError },
  ] = useQueries({
    queries: [
      {
        queryKey: ["employees", id],
        queryFn: () =>
          getEmployee({
            skip: 0,
            companyId: id,
            limit: 100,
          }),
      },
      {
        queryKey: ["roles"],
        queryFn: () => getRoles(),
      },
      {
        queryKey: ["status"],
        queryFn: () => getStatus(),
      },
    ],
  });

  if (employeeError || rolesError || statusError) {
    const isAxiosError = (
      error: unknown
    ): error is { response?: { data?: { message?: string } } } => {
      return typeof error === "object" && error !== null && "response" in error;
    };

    if (
      (isAxiosError(employeeError) &&
        employeeError?.response?.data?.message === "LOGGED_OUT") ||
      (isAxiosError(rolesError) &&
        rolesError?.response?.data?.message === "LOGGED_OUT") ||
      (isAxiosError(statusError) &&
        statusError?.response?.data?.message === "LOGGED_OUT")
    ) {
      console.log("clearing storage due to logged out");
      clearLocalStorage();
      window.location.reload();
    }
  }

  // Mutation
  const {
    mutate: createEmployeeMutation,
    isPending: createEmployeeMutationPending,
  } = useMutation({
    mutationFn: (data: MutateEmployee) => createEmployee(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["employees", id],
      });
      toast({
        title: "ສຳເລັດ",
        description: "ສ້າງຂໍ້ມູນບັນຊີ້ເຂົ້າສູ່ລະບົບສຳເລັດ",
      });
      handleClosePopup();
    },
    onError: () => {
      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ມີບາງຢ່າງຜິດພາດ ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
        variant: "destructive",
      });
    },
  });

  const {
    mutate: updateEmployeeMutation,
    isPending: updateEmployeeMutationPending,
  } = useMutation({
    mutationFn: (data: MutateEmployee) =>
      updateEmployee(selectedData?.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["employees", id],
      });
      toast({
        title: "ສຳເລັດ",
        description: "ແກ້ໄຂຂໍ້ມູນບັນຊີ້ເຂົ້າສູ່ລະບົບສຳເລັດ",
      });

      handleClosePopup();
    },
    onError: () => {
      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ມີບາງຢ່າງຜິດພາດ ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
        variant: "destructive",
      });
    },
  });

  const {
    mutate: updateEmployeeStatusMutation,
    isPending: updateEmployeeStatusMutationPending,
  } = useMutation({
    mutationFn: (data: { id: string; statusId: string }) =>
      updateEmployeeStatus(data.id, { statusId: data.statusId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["employees", id],
      });
      toast({
        title: "ສຳເລັດ",
        description: "ແກ້ໄຂຂໍ້ມູນບັນຊີ້ເຂົ້າສູ່ລະບົບສຳເລັດ",
      });

      handleClosePopup();
    },
    onError: () => {
      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ມີບາງຢ່າງຜິດພາດ ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
        variant: "destructive",
      });
    },
  });

  // Functions

  const handleClosePopup = () => {
    setOpenModal(false);
    setSelectedData({} as EmployeeList);
  };

  const handleOnSubmit = (
    values: MutateEmployee | MutateEmployeeOptionalPassword
  ) => {
    if (selectedData?.id) {
      updateEmployeeMutation(values as MutateEmployee);
    } else {
      createEmployeeMutation(values as MutateEmployee);
    }
  };

  // Data
  const isLoading = employeeLoading || roleLoading || statusLoading;
  const mutationLoading =
    createEmployeeMutationPending || updateEmployeeMutationPending;

  const employees = employeesRes?.data as EmployeeList[];
  const roles = rolesRes?.data as Roles[];
  const status = statusRes?.data;

  console.log(rolesRes?.data, "Role");

  return (
    <TooltipProvider>
      <div className="space-y-[24px]">
        <div className="text-[18px]">
          <h2 className="text-zinc-950">ບັນຊີເຂົ້າສູ່ລະບົບ</h2>
          <p className="text-zinc-500">ຕາຕະລາງຂໍ້ມູນບັນຊີເຂົ້າສູ່ລະບົບ</p>

          <Separator className="my-6" />
        </div>

        <div className="flex justify-end w-full">
          {company_management.isWrite && (
            <Button onClick={() => setOpenModal(true)}>
              <PlusCircle className="mr-2" />
              ເພີ່ມບັນຊີ
            </Button>
          )}
        </div>

        {/* FIXME: Replace with Data Table Component */}
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <Table className="border rounded-md ">
            <TableHeader className="">
              <TableRow>
                <TableHead>ລຳດັບ</TableHead>
                <TableHead>ໄອດີຜູ້ໃຊ້</TableHead>
                <TableHead>ຊື່ ແລະ ນາມສະກຸນ</TableHead>
                <TableHead>ປະເພດບັນຊີ</TableHead>
                <TableHead>ສະຖານະ</TableHead>
                <TableHead>ຈັດການ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees?.map((item, index) => (
                <TableRow key={item?.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{item?.userCode}</TableCell>
                  <TableCell>
                    {item?.firstName} {item.lastName}
                  </TableCell>
                  <TableCell>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help truncate block max-w-32">
                          {item?.role?.description}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="flex items-center justify-center">
                        <p className="max-w-xs text-center">
                          {item?.role?.description}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        item?.status?.status === "ACTIVE"
                          ? "default"
                          : "secondary"
                      }
                    >
                      {item?.status?.description}
                    </Badge>
                  </TableCell>
                  <TableCell className="flex items-center gap-2">
                    {company_management.isUpdate && (
                      <Button
                        variant={"outline"}
                        size={"icon"}
                        onClick={() => {
                          setSelectedData(item);
                          setOpenModal(true);
                        }}
                      >
                        <Pen />
                      </Button>
                    )}
                    {company_management.isUpdateData && (
                      <AlertModal
                        title="ແກ້ໄຂສະຖານະການໃຊ້ງານ"
                        desc={
                          <div>
                            <Label htmlFor="statusId">
                              ເລືອກຕາມສິດການນຳໃຊ້{" "}
                              <span className="text-red-500">*</span>
                            </Label>
                            <Select
                              name="statusId"
                              value={statusId}
                              defaultValue={statusId}
                              onValueChange={(value) => setStatusId(value)}
                              required
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="ເລຶອກຕາມສິດການນຳໃຊ້" />
                              </SelectTrigger>
                              <SelectContent>
                                {status?.map(
                                  (item: {
                                    id: string;
                                    description: string;
                                  }) => (
                                    <SelectItem value={item?.id} key={item?.id}>
                                      {item?.description}
                                    </SelectItem>
                                  )
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        }
                        triggerComponent={
                          <Button
                            variant={"outline"}
                            // size={"icon"}
                            onClick={() => {
                              setStatusId(item?.statusId);
                            }}
                            disabled={updateEmployeeStatusMutationPending}
                          >
                            {updateEmployeeStatusMutationPending
                              ? "ກຳລັງດຳເນີນການ"
                              : "ແກ້ໄຂສະຖານະ"}
                          </Button>
                        }
                        confirmFunction={() => {
                          updateEmployeeStatusMutation({
                            id: item?.id,
                            statusId: statusId,
                          });
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Dialog open={openModal} onOpenChange={handleClosePopup}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>ເພີ່ມຂໍ້ມູນບັນຊີ້ເຂົ້າສູ່ລະບົບ</DialogTitle>
            </DialogHeader>
            <Formik
              initialValues={{
                companyId: id,
                firstName: selectedData?.firstName || "",
                lastName: selectedData?.lastName || "",
                userCode: selectedData?.userCode || "",
                password: selectedData?.password || "",
                roleId: selectedData?.roleId || "",
                birthDate:
                  formatDate({
                    date: selectedData?.birthDate || "",
                    format: "YYYY-MM-DD",
                  }) || "",

                // accountType: selectedData?.accountType || "",
              }}
              onSubmit={(values: MutateEmployee) => {
                console.log(values);

                // For updates, password is optional
                const submitData = { ...values };
                if (!submitData.password && selectedData?.id) {
                  // Remove password field for updates when it's empty
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { password, ...dataWithoutPassword } = submitData;
                  handleOnSubmit(
                    dataWithoutPassword as MutateEmployeeOptionalPassword
                  );
                } else {
                  handleOnSubmit(submitData);
                }
              }}
            >
              {({ handleSubmit, values, setFieldValue }) => {
                // Validation logic for required fields
                const isFormValid = () => {
                  const requiredFields = [
                    "firstName",
                    "lastName",
                    "roleId",
                    "userCode",
                    "birthDate",
                  ];

                  // Check if all required fields have values
                  const allRequiredFieldsFilled = requiredFields.every(
                    (field) =>
                      values[field as keyof typeof values] &&
                      String(values[field as keyof typeof values]).trim() !== ""
                  );

                  // For new accounts, password is also required
                  if (!selectedData?.id) {
                    return (
                      allRequiredFieldsFilled &&
                      values.password &&
                      values.password.trim() !== ""
                    );
                  }

                  // For existing accounts, password is optional
                  return allRequiredFieldsFilled;
                };

                return (
                  <form onSubmit={handleSubmit} className="space-y-[16px]">
                    <div>
                      <Label>
                        ຊື່ <span className="text-red-500">*</span>
                      </Label>
                      <Field as={Input} name="firstName" required />
                    </div>
                    <div>
                      <Label>
                        ນາມສະກຸນ <span className="text-red-500">*</span>
                      </Label>
                      <Field as={Input} name="lastName" required />
                    </div>
                    <DatePicker
                      label={"ວັນເດືອນປີເກີດ"}
                      name="birthDate"
                      value={values.birthDate}
                      onChange={(value) => setFieldValue("birthDate", value)}
                      required
                    />
                    <div>
                      <Label htmlFor="roleId">
                        ເລືອກຕາມສິດການນຳໃຊ້{" "}
                        <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        name="roleId"
                        value={values.roleId}
                        defaultValue={values.roleId}
                        onValueChange={(value) =>
                          setFieldValue("roleId", value)
                        }
                        required
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="ເລຶອກຕາມສິດການນຳໃຊ້" />
                        </SelectTrigger>
                        <SelectContent>
                          {roles?.some(
                            (role) => role.roleType === "STATE_ENTERPRISE"
                          ) && (
                            <>
                              <div className="px-2 py-1.5">
                                <p className="text-sm font-medium text-gray-900">
                                  ບົດບາດ
                                </p>
                              </div>
                              {roles
                                ?.filter(
                                  (role) => role.roleType === "STATE_ENTERPRISE"
                                )
                                ?.map((role) => (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <SelectItem
                                        value={role?.id}
                                        key={role?.id}
                                      >
                                        <span className="cursor-help truncate block max-w-full">
                                          {role?.role}
                                        </span>
                                      </SelectItem>
                                    </TooltipTrigger>
                                    <TooltipContent className="flex items-center justify-center">
                                      <div className="max-w-xs text-center">
                                        <p className="font-medium">
                                          {role?.description || "ບໍ່ມີ"}
                                        </p>
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                ))}
                              {roles?.some(
                                (role) => role.roleType === "STATE_ENTERPRISE"
                              ) && <Separator className="my-1" />}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>
                        ລະຫັດຜູ້ໃຊ້ <span className="text-red-500">*</span>
                      </Label>
                      <Field as={Input} name="userCode" required />
                    </div>

                    <div>
                      <Label>
                        ລະຫັດເຂົ້າສູ່ລະບົບ{" "}
                        {!selectedData.id ? (
                          <span className="text-red-500">*</span>
                        ) : null}
                      </Label>
                      <Field
                        as={Input}
                        name="password"
                        required={!selectedData.id}
                      />
                    </div>

                    <div className="flex items-center justify-center w-full gap-4">
                      <Button
                        type="button"
                        variant={"outline"}
                        onClick={() => setOpenModal(false)}
                      >
                        ຍົກເລີກ
                      </Button>
                      <Button
                        type="submit"
                        disabled={mutationLoading || !isFormValid()}
                      >
                        {mutationLoading
                          ? "ກຳລັງດຳເນີນການ..."
                          : selectedData?.id
                          ? "ແກ້ໄຂ"
                          : "ບັນທຶກ"}
                      </Button>
                    </div>
                  </form>
                );
              }}
            </Formik>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
